import * as React from "react";
import { Link, useSearchParams } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckList, Field, PageHeader, StatusPill, Toggle, type CheckItem, type StatusTone } from "@ozpanel/kit";
import { Button, Panel, Section, StateView, formatRelative, useAuth, useToast } from "@ozpanel/ui";
import { PhasedSteps, type JobStepView } from "./PhasedSteps";

// AddNodeScreen — bringing a machine into the cluster, end to end, on one screen.
//
// The join existed server-side and was reachable from v2 only through the generic operations form —
// no preflight, no way to see the joiner's own steps, and a "Resume" on a join that the server can
// only refuse. The legacy console had the only real form. This replaces it:
//
//   1. WHO drives it. Only the verified master signs a joiner's identity and mints its mesh address,
//      and the server refuses a join anywhere else. So this screen asks /cluster/roles who holds the
//      master role and, on any other node, says so and links to the master's console — it never sends
//      a join to a node that can only answer 409.
//   2. PREFLIGHT, measured on the target over SSH before anything changes (POST /cluster/preflight):
//      OS, architecture, root, memory, a bare machine, the name in public DNS, the name and address
//      free in the registry. "Send" stays disabled until a preflight of EXACTLY these inputs has no
//      refusal; editing any field discards it.
//   3. WATCH both halves: the master's 8 SSH steps (GET /cluster/join/{job}) and, once the joiner's
//      panel takes over, its own bootstrap (linked by the server as bootstrap_job).
//   4. ON FAILURE: a join cannot be resumed — its passwords lived in memory only — so it is SENT
//      AGAIN, with the passwords asked for again. A failed joiner bootstrap is resumed, and the
//      server relays that to the joiner.
//
// Passwords live in this component's state only until the join is sent, and are never shown back.

interface RoleRow {
  node: string;
  authority: string;
  stale?: boolean;
}
interface NodeRow {
  name: string;
  fqdn: string;
  self: boolean;
  real_ip?: string;
  floating_ip?: string;
}
interface PreflightCheck {
  name: string;
  state: "ok" | "warn" | "fail" | "unknown";
  blocking: boolean;
  detail: string;
}
interface PreflightAnswer {
  ok: boolean;
  refusals?: string[] | null;
  checks: PreflightCheck[];
  note?: string;
}
interface JobData {
  job_id: string;
  node: string;
  status: string;
  steps: JobStepView[];
  error?: string;
  started_at?: string;
  completed_at?: string;
  bootstrap_job?: { job_id: string; status: string };
  bootstrap_job_unread?: string;
}
interface Readiness {
  ready: boolean;
  blocking?: string[] | null;
  checks?: { module: string; name: string; ready: boolean; detail?: string }[] | null;
}

type Form = {
  name: string;
  fqdn: string;
  real_ip: string;
  ssh_port: string;
  ssh_password: string;
  new_password: string;
  floating_ip: string;
  hub_ip: string;
  hub_ssh_port: string;
  hub_password: string;
  tinc_port: string;
};
const emptyForm: Form = { name: "", fqdn: "", real_ip: "", ssh_port: "", ssh_password: "", new_password: "", floating_ip: "", hub_ip: "", hub_ssh_port: "", hub_password: "", tinc_port: "" };

/** The master's join steps (cluster/steps.go joinOrchestration), grouped as the job records them. */
const JOIN_PHASES: Record<number, string> = {
  0: "Master: reach and register the machine",
  1: "Master: provision, enrol, mesh",
  2: "The joiner runs its own bootstrap",
  3: "Verify on the running systems",
};

const statusTone = (s: string): StatusTone =>
  s === "done" ? "ok" : s === "failed" ? "bad" : s === "running" ? "accent" : "neutral";

export function AddNodeScreen() {
  const { client } = useAuth();
  const [params, setParams] = useSearchParams();
  const jobId = params.get("job");

  const roles = useQuery({
    queryKey: ["cluster-roles"],
    queryFn: () => client.apiFetch<{ nodes: RoleRow[] | null }>("/cluster/roles"),
    retry: false,
  });
  const nodes = useQuery({
    queryKey: ["cluster-nodes"],
    queryFn: () => client.apiFetch<{ items: NodeRow[] | null }>("/cluster/nodes"),
    retry: false,
  });

  const self = (nodes.data?.items ?? []).find((n) => n.self);
  const holders = (roles.data?.nodes ?? []).filter((r) => r.authority === "master_holder" || r.authority === "master_holder_NO_KEY");
  const master = holders.length === 1 ? holders[0] : undefined;
  const masterNode = master ? (nodes.data?.items ?? []).find((n) => n.name === master.node) : undefined;
  const here = !!master && !!self && master.node === self.name && master.authority === "master_holder";

  // Who drives a join, as measured — never assumed.
  let driver: React.ReactNode = null;
  if (roles.isLoading || nodes.isLoading) driver = null;
  else if (roles.error || nodes.error) {
    driver = (
      <Panel className="border-[color-mix(in_oklab,var(--ozp-warn)_38%,transparent)] p-4 text-[13px]">
        Which node holds the master role could not be read ({String((roles.error ?? nodes.error) as Error)}), so this
        screen cannot tell whether a join sent from here would be accepted. Joins are driven by the master only.
      </Panel>
    );
  } else if (holders.length !== 1) {
    driver = (
      <Panel className="border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-4 text-[13px]">
        {holders.length === 0
          ? "No node reports holding the master role, so no node can sign a new member's identity. Resolve the master role first (Cluster → Master)."
          : `${holders.length} nodes report holding the master role (${holders.map((h) => h.node).join(", ")}) — the cluster disagrees with itself. Resolve that before adding a node.`}
      </Panel>
    );
  } else if (master && master.authority === "master_holder_NO_KEY") {
    driver = (
      <Panel className="border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-4 text-[13px]">
        {master.node} is the declared master but does not hold the CA key, so it cannot sign a new member's
        identity. No node can drive a join until the key is back on the master.
      </Panel>
    );
  } else if (!here && master) {
    const url = masterNode?.fqdn ? `https://${masterNode.fqdn}${import.meta.env.BASE_URL}nodes/add` : "";
    driver = (
      <Panel className="p-4 text-[13px] leading-relaxed">
        A join is driven by the cluster master, which signs the new node's identity and gives it a mesh
        address. The master is <span className="font-mono">{master.node}</span>
        {master.stale ? " (its role report is stale — it has not reported recently)" : ""}; this console is{" "}
        <span className="font-mono">{self?.name ?? "another node"}</span>, where the join would be refused.
        {url ? (
          <div className="mt-3">
            <a className="text-accent hover:underline" href={url}>
              Open {master.node}'s console to add a node →
            </a>
            <div className="mt-1 font-mono text-[11.5px] text-muted">{url}</div>
          </div>
        ) : (
          <div className="mt-2 text-muted">{master.node}'s FQDN is not in the registry, so no link can be offered.</div>
        )}
      </Panel>
    );
  }

  return (
    <div>
      <PageHeader
        crumbs={[{ label: "Cluster", to: "/modules/cluster" }, { label: "Add a node" }]}
        title="Add a node"
        subtitle="Preflight a bare machine, join it to the cluster, and watch both halves of the join."
        actions={
          jobId ? (
            <Button variant="ghost" onClick={() => setParams({})}>
              New join
            </Button>
          ) : undefined
        }
      />
      <div className="space-y-4">
        {driver}
        {jobId ? (
          <JoinWatch
            jobId={jobId}
            canResend={here}
            onResend={(name) => {
              // The non-secret inputs come back from the registry the failed attempt wrote (the job row
              // keeps only the name); the passwords and the SSH port are asked for again.
              const n = (nodes.data?.items ?? []).find((x) => x.name === name);
              setParams({
                resend: JSON.stringify({ name, fqdn: n?.fqdn && n.fqdn !== name ? n.fqdn : "", real_ip: n?.real_ip ?? "", floating_ip: n?.floating_ip ?? "" }),
              });
            }}
          />
        ) : here ? (
          <JoinForm />
        ) : null}
        <RecentJoins />
      </div>
    </div>
  );
}

// ── the form, preflight and send ────────────────────────────────────────────────────────────────

function JoinForm() {
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const resend = React.useMemo<Partial<Form> | null>(() => {
    try {
      return JSON.parse(params.get("resend") ?? "null") as Partial<Form> | null;
    } catch {
      return null;
    }
  }, [params]);
  const [f, setF] = React.useState<Form>({ ...emptyForm, ...(resend ?? {}), ssh_password: "", new_password: "", hub_password: "" });
  const [firstLogin, setFirstLogin] = React.useState(false);
  const [pf, setPf] = React.useState<{ key: string; answer: PreflightAnswer } | null>(null);
  const [pfBusy, setPfBusy] = React.useState(false);
  const [pfErr, setPfErr] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);
  const [sendErr, setSendErr] = React.useState<string | null>(null);
  const [ackNotReady, setAckNotReady] = React.useState(false);

  const readiness = useQuery({
    queryKey: ["join-readiness"],
    queryFn: () => client.apiFetch<Readiness>("/ozpanel/join-readiness"),
    retry: false,
  });
  const plan = useQuery({
    queryKey: ["cluster-join-plan", firstLogin],
    queryFn: () =>
      client.apiFetch<{ steps: JobStepView[]; total: number; phases: Record<string, string> }>(
        `/cluster/join/plan?with_first_login=${firstLogin ? "true" : "false"}`,
      ),
    retry: false,
  });

  const body = { ...f, new_password: firstLogin ? f.new_password : "" };
  // The preflight belongs to EXACTLY the inputs it measured. Kept in memory only.
  const key = JSON.stringify(body);
  const current = pf && pf.key === key ? pf.answer : null;
  const set = (k: keyof Form) => (v: string) => setF((x) => ({ ...x, [k]: v }));

  async function preflight() {
    setPfBusy(true);
    setPfErr(null);
    try {
      const a = await client.apiFetch<PreflightAnswer>("/cluster/preflight", { method: "POST", body: JSON.stringify(body) });
      setPf({ key, answer: a });
    } catch (e) {
      setPfErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPfBusy(false);
    }
  }

  async function send() {
    setSending(true);
    setSendErr(null);
    try {
      const r = await client.apiFetch<{ job_id: string }>("/cluster/join", { method: "POST", body: JSON.stringify(body) });
      // The passwords leave this component the moment the server has them.
      setF((x) => ({ ...x, ssh_password: "", new_password: "", hub_password: "" }));
      setPf(null);
      notify({ kind: "success", title: `Join of ${f.name} started`, body: r.job_id });
      void qc.invalidateQueries({ queryKey: ["cluster-jobs"] });
      setParams({ job: r.job_id });
    } catch (e) {
      setSendErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  const notReady = readiness.data && !readiness.data.ready;
  const readinessUnread = !!readiness.error;
  const canSend =
    !!current && current.ok && !sending && (!notReady || ackNotReady) && (!readinessUnread || ackNotReady);

  const items: CheckItem[] = (current?.checks ?? []).map((c, i) => ({
    key: `${i}-${c.name}`,
    label: c.name,
    state: c.state,
    detail: c.detail,
    because: c.state === "fail" && c.blocking ? "Refuses the join." : c.state === "unknown" && c.blocking ? "Not measured — refuses the join until it is." : undefined,
  }));

  return (
    <>
      {resend && (
        <Panel className="p-3 text-[12.5px] leading-relaxed text-muted">
          Sending the join for <span className="font-mono">{resend.name}</span> again. The passwords were held in memory
          for the failed attempt only, so they are asked for again. Every step is idempotent: the master's steps re-run,
          and the joiner's own bootstrap resumes from the step that failed.
        </Panel>
      )}
      <Section title="The machine">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Node name" value={f.name} onChange={set("name")} mono placeholder="node12"
            help="A short lowercase name. It becomes the node's mesh name and its per-node account names." />
          <Field label="FQDN" value={f.fqdn} onChange={set("fqdn")} mono placeholder={f.name ? `${f.name}.example.com` : "node12.example.com"}
            help="The name its panel answers on. Its A record must already point at the floating IP if one is given, else the real IP." />
          <Field label="Real IP (SSH target)" value={f.real_ip} onChange={set("real_ip")} mono placeholder="203.0.113.12" />
          <Field label="SSH port" value={f.ssh_port} onChange={set("ssh_port")} mono placeholder="22" />
          <Field label="Root SSH password" type="password" value={f.ssh_password} onChange={set("ssh_password")} mono
            help="Used for this join only — held in the master's memory while the join runs, never stored or shown." />
          <Field label="Floating IP (optional)" value={f.floating_ip} onChange={set("floating_ip")} mono placeholder="none"
            help="Only if the node will answer the world on an address it does not hold itself." />
        </div>
        {f.floating_ip.trim() !== "" && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <p className="sm:col-span-2 text-sm text-muted-foreground">
              The node meets the cluster FROM its floating IP, so the join puts it in force first. Already in force on the
              node: leave the hub empty — the join measures it. Not yet: give the hub that holds the address, and the join
              builds the tunnel and proves it before going on.
            </p>
            <Field label="Hub address" value={f.hub_ip} onChange={set("hub_ip")} mono placeholder="the VPS holding the floating IP" />
            <Field label="Hub SSH port" value={f.hub_ssh_port} onChange={set("hub_ssh_port")} mono placeholder="22" />
            <Field label="Hub root password" type="password" value={f.hub_password} onChange={set("hub_password")} mono
              help="Held in the master's memory for this join only; on the node it is removed before the tunnel steps run." />
            <Field label="Tunnel port" value={f.tinc_port} onChange={set("tinc_port")} mono placeholder="auto (659 up, a port the hub does not use)"
              help="The tunnel's own port on the hub. A hub shared with other networks needs one of its own." />
          </div>
        )}
        <div className="mt-3">
          <Toggle
            checked={firstLogin}
            onChange={setFirstLogin}
            label="The provider forces a password change on first login (e.g. a fresh Hetzner machine)"
            help="The join's first step changes the root password to the one below, then uses it for everything after."
          />
          {firstLogin && (
            <div className="mt-2 max-w-sm">
              <Field label="New root password" type="password" value={f.new_password} onChange={set("new_password")} mono />
            </div>
          )}
        </div>
      </Section>

      <Section
        title="Preflight"
        hint={current ? (current.ok ? "nothing refuses the join" : `${current.refusals?.length ?? 0} refusal(s)`) : "measured on the target, nothing is changed"}
        actions={
          <Button size="sm" onClick={() => void preflight()} disabled={pfBusy}>
            {pfBusy ? "Measuring…" : current ? "Run again" : "Run preflight"}
          </Button>
        }
        bodyClassName="p-0"
      >
        {pfErr && <div className="px-4 py-3 text-[12.5px] text-crit">{pfErr}</div>}
        {!current && !pfErr && (
          <div className="px-4 py-3 text-[12.5px] text-muted">
            {pf ? "The inputs changed since the last preflight — run it again for these values." : "Not run yet. The join can be sent once a preflight of these exact inputs has nothing refusing it."}
          </div>
        )}
        {current && <CheckList items={items} />}
        {current?.note && <div className="border-t border-line px-4 py-2 text-[11.5px] text-muted">{current.note}</div>}
      </Section>

      <Section title="The cluster is ready to take a node" bodyClassName="p-0">
        <StateView loading={readiness.isLoading} error={readiness.error} errorContext="join readiness">
          <CheckList
            summarise
            items={(readiness.data?.checks ?? []).map((c, i) => ({
              key: `${c.module}-${i}`,
              label: `${c.module}: ${c.name}`,
              state: c.ready ? "ok" : "fail",
              detail: c.detail,
            }))}
            emptyMessage="No module declares a readiness check."
          />
        </StateView>
        {(notReady || readinessUnread) && (
          <div className="border-t border-line px-4 py-3">
            <Toggle
              checked={ackNotReady}
              onChange={setAckNotReady}
              label={readinessUnread ? "Send the join although readiness could not be read" : "Send the join although the cluster is not join-ready"}
              consequence="An unready cluster can be damaged by a join, not just the new node: shared accounts rewritten, a Redis master that cannot be replicated from, mail DNS that does not span the new node."
            />
          </div>
        )}
      </Section>

      <Section title="Send the join">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="accent" disabled={!canSend} onClick={() => void send()}>
            {sending ? "Sending…" : `Join ${f.name || "the node"}`}
          </Button>
          <span className="text-[12px] text-muted">
            {!current
              ? "Run the preflight for these inputs first."
              : !current.ok
                ? "The preflight refuses this join — fix what it names and run it again."
                : notReady && !ackNotReady
                  ? "The cluster is not join-ready (above)."
                  : "The master drives eight steps over SSH, then the node runs its own bootstrap. Both are watched here."}
          </span>
        </div>
        {sendErr && <div className="mt-3 rounded border border-line bg-critsoft px-3 py-2 text-[12.5px] text-crit">{sendErr}</div>}
        {plan.data && (
          <details className="mt-4">
            <summary className="cursor-pointer text-[12px] text-muted">
              What the node's own bootstrap will run ({plan.data.total} steps)
            </summary>
            <div className="mt-2">
              <PhasedSteps steps={plan.data.steps.map((s) => ({ ...s, status: "pending" }))} phases={numberKeys(plan.data.phases)} />
            </div>
          </details>
        )}
      </Section>
    </>
  );
}

function numberKeys(m: Record<string, string>): Record<number, string> {
  const out: Record<number, string> = {};
  for (const [k, v] of Object.entries(m ?? {})) out[Number(k)] = v;
  return out;
}

// ── watching both halves ───────────────────────────────────────────────────────────────────────

function useJob(id: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: ["cluster-job", id],
    enabled: !!id,
    queryFn: () => client.apiFetch<JobData>(`/cluster/join/${encodeURIComponent(id ?? "")}`),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return !s || s === "running" || s === "pending" ? 2000 : false;
    },
  });
}

function JoinWatch({ jobId, canResend, onResend }: { jobId: string; canResend: boolean; onResend: (name: string) => void }) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const join = useJob(jobId);
  const bootId = join.data?.bootstrap_job?.job_id;
  const boot = useJob(bootId);
  // The joiner's job keeps changing after the master's step that waits on it has failed (a resume runs
  // on the joiner), so the master's job is re-read while the joiner's runs.
  React.useEffect(() => {
    if (boot.data?.status === "running") void qc.invalidateQueries({ queryKey: ["cluster-job", jobId] });
  }, [boot.data?.status, boot.dataUpdatedAt, jobId, qc]);
  const plan = useQuery({
    queryKey: ["cluster-join-plan", false],
    queryFn: () => client.apiFetch<{ phases: Record<string, string> }>("/cluster/join/plan"),
    retry: false,
  });
  const [resuming, setResuming] = React.useState(false);
  const [resumeResult, setResumeResult] = React.useState<{ ok: boolean; text: string } | null>(null);

  const j = join.data;
  const failedStep = j?.steps.find((s) => s.status === "failed");
  const done = j?.steps.filter((s) => s.status === "done" || s.status === "skipped").length ?? 0;

  async function resume() {
    if (!bootId) return;
    setResuming(true);
    setResumeResult(null);
    try {
      const r = await client.apiFetch<{ ran_on?: string }>(`/cluster/bootstrap/${encodeURIComponent(bootId)}/resume`, { method: "POST" });
      setResumeResult({ ok: true, text: `Resumed on ${r.ran_on ?? j?.node ?? "the joiner"} from the failed step.` });
      void qc.invalidateQueries({ queryKey: ["cluster-job", bootId] });
    } catch (e) {
      setResumeResult({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setResuming(false);
    }
  }

  return (
    <StateView loading={join.isLoading} error={join.error} errorContext={`join job ${jobId}`}>
      {j && (
        <div className="space-y-4">
          <Panel className="p-4">
            <div className="flex flex-wrap items-center gap-2 text-[13px]">
              <span className="font-medium">Join of {j.node}</span>
              <StatusPill tone={statusTone(j.status)}>{j.status}</StatusPill>
              <span className="text-muted">
                master: {done} of {j.steps.length} steps
                {j.started_at ? ` · started ${formatRelative(j.started_at)}` : ""}
              </span>
              <span className="ml-auto font-mono text-[11.5px] text-faint">{j.job_id}</span>
            </div>
            {j.status === "failed" && (
              <div className="mt-3 space-y-3">
                <pre className="max-h-48 overflow-auto rounded border border-line bg-critsoft p-3 font-mono text-[12px] whitespace-pre-wrap text-crit">
                  {failedStep ? `${failedStep.name}: ` : ""}
                  {j.error || failedStep?.detail || "the job failed without recording a reason"}
                </pre>
                <FailureActions
                  join={j}
                  boot={boot.data}
                  canResend={canResend}
                  onResend={() => onResend(j.node)}
                  onResume={() => void resume()}
                  resuming={resuming}
                />
                {resumeResult && (
                  <div className={`rounded border border-line px-3 py-2 text-[12.5px] ${resumeResult.ok ? "text-ok" : "bg-critsoft text-crit"}`}>
                    {resumeResult.text}
                  </div>
                )}
              </div>
            )}
            {j.status === "done" && (
              <p className="mt-3 text-[12.5px] text-muted">
                The master's verify step ran on the live systems — its report is under "verify" below. The node now
                appears under Cluster → Nodes, with its addresses as its peers measure them.
              </p>
            )}
          </Panel>

          <div className="grid gap-4 xl:grid-cols-2">
            <div>
              <h2 className="mb-2 text-[12px] font-medium tracking-wide text-muted uppercase">The master's steps (over SSH)</h2>
              <PhasedSteps steps={j.steps} phases={JOIN_PHASES} />
            </div>
            <div>
              <h2 className="mb-2 text-[12px] font-medium tracking-wide text-muted uppercase">
                {j.node}'s own bootstrap
                {boot.data && (
                  <span className="ml-2 normal-case">
                    <StatusPill tone={statusTone(boot.data.status)}>{boot.data.status}</StatusPill>
                  </span>
                )}
              </h2>
              {j.bootstrap_job_unread ? (
                <Panel className="p-3 text-[12.5px] text-warn">The joiner's job could not be read: {j.bootstrap_job_unread}</Panel>
              ) : !bootId ? (
                <Panel className="p-3 text-[12.5px] leading-relaxed text-muted">
                  {j.steps.some((s) => s.name === "bootstrap_node" && s.status === "running")
                    ? "The joiner is running the first half of its bootstrap (packages, MariaDB, the schema) — its output streams into the master's bootstrap_node step. Its own job appears here once its panel takes over."
                    : "No bootstrap job from the joiner yet — it starts at the master's bootstrap_node step."}
                </Panel>
              ) : (
                <StateView loading={boot.isLoading} error={boot.error} errorContext={`the joiner's job ${bootId}`}>
                  {boot.data && (
                    <>
                      {boot.data.status === "failed" && boot.data.error && (
                        <pre className="mb-3 max-h-40 overflow-auto rounded border border-line bg-critsoft p-3 font-mono text-[12px] whitespace-pre-wrap text-crit">
                          {boot.data.error}
                        </pre>
                      )}
                      <PhasedSteps steps={boot.data.steps} phases={numberKeys(plan.data?.phases ?? {})} />
                    </>
                  )}
                </StateView>
              )}
            </div>
          </div>
        </div>
      )}
    </StateView>
  );
}

/** What can be done about a failed join, decided by WHICH half failed. */
function FailureActions({
  join,
  boot,
  canResend,
  onResend,
  onResume,
  resuming,
}: {
  join: JobData;
  boot?: JobData;
  canResend: boolean;
  onResend: () => void;
  onResume: () => void;
  resuming: boolean;
}) {
  const bootFailed = boot?.status === "failed";
  return (
    <div className="flex flex-wrap items-start gap-3">
      {bootFailed && (
        <div className="max-w-md">
          <Button variant="accent" disabled={resuming} onClick={onResume}>
            {resuming ? "Resuming…" : `Resume ${join.node}'s bootstrap`}
          </Button>
          <p className="mt-1 text-[11.5px] leading-relaxed text-muted">
            The joiner's own bootstrap failed. Resuming re-runs it on {join.node} from the failed step (relayed to it over
            the node-to-node channel — its panel must be running). Fix the cause it reports first. The master's verify
            step does not re-run; send the join again afterwards to have it verified.
          </p>
        </div>
      )}
      {boot?.status === "running" && (
        <p className="text-[12px] text-muted">The joiner's bootstrap is running again — follow it on the right.</p>
      )}
      <div className="max-w-md">
        <Button variant={bootFailed ? "ghost" : "accent"} disabled={!canResend} onClick={onResend}>
          Send the join again…
        </Button>
        <p className="mt-1 text-[11.5px] leading-relaxed text-muted">
          {canResend
            ? "A join cannot be resumed: its SSH passwords were held in memory only. Sending it again asks for them, re-runs the preflight, and continues — every step is idempotent and the joiner's bootstrap resumes from where it failed."
            : "Only the master can send a join."}
        </p>
      </div>
    </div>
  );
}

function RecentJoins() {
  const { client } = useAuth();
  const jobs = useQuery({
    queryKey: ["cluster-jobs"],
    queryFn: () => client.apiFetch<{ items: { job_id: string; node: string; status: string; started_at: string }[] | null }>("/cluster/join"),
    refetchInterval: 15000,
  });
  const joins = (jobs.data?.items ?? []).filter((j) => j.job_id.startsWith("join_")).slice(0, 8);
  return (
    <Section title="Recent joins" bodyClassName="p-0">
      <StateView loading={jobs.isLoading} error={jobs.error} errorContext="join jobs">
        {joins.length === 0 ? (
          <div className="px-4 py-4 text-[12.5px] text-muted">No join has been recorded.</div>
        ) : (
          <div className="divide-y divide-line">
            {joins.map((j) => (
              <Link key={j.job_id} to={`?job=${encodeURIComponent(j.job_id)}`} className="flex items-center gap-3 px-4 py-2 text-[12.5px] hover:bg-bg2">
                <StatusPill tone={statusTone(j.status)}>{j.status}</StatusPill>
                <span className="font-mono">{j.node}</span>
                <span className="text-muted">{formatRelative(j.started_at)}</span>
                <span className="ml-auto font-mono text-[11.5px] text-faint">{j.job_id}</span>
              </Link>
            ))}
          </div>
        )}
      </StateView>
    </Section>
  );
}
